import type { Site, SitePatch } from "@/api/zero/sites";

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
export const SEND_WINDOW_STEP_MINUTES = 15;
export const SEND_WINDOW_EARLIEST_MINUTES = 9 * MINUTES_PER_HOUR;
export const SEND_WINDOW_LATEST_MINUTES = 20 * MINUTES_PER_HOUR;

export interface SendWindowFormValues {
  start: number | null;
  end: number | null;
}

export function emptySendWindowFormValues(): SendWindowFormValues {
  return { start: null, end: null };
}

export function parseSendWindowSelection(raw: string | number): number | null {
  const parsed = typeof raw === "number" ? raw : Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function toSendWindowFormValues(site: Site): SendWindowFormValues {
  return {
    start: site.sms_send_window_start ?? null,
    end: site.sms_send_window_end ?? null,
  };
}

export function isSendWindowDirty(
  old: SendWindowFormValues,
  current: SendWindowFormValues,
): boolean {
  return old.start !== current.start || old.end !== current.end;
}

export function isValidSendWindowMinute(value: number | null): value is number {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= 0 &&
    value < MINUTES_PER_DAY
  );
}

export function formatSendWindowTime(minutesOfDay: number): string {
  const hour = Math.floor(minutesOfDay / MINUTES_PER_HOUR);
  const minute = minutesOfDay % MINUTES_PER_HOUR;
  const period = hour < 12 ? "AM" : "PM";
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelveHour}:${String(minute).padStart(2, "0")} ${period}`;
}

export function resolveSendWindowTime(value: number | null): string {
  if (!isValidSendWindowMinute(value)) return "Not set";
  return formatSendWindowTime(value);
}

export function buildSendWindowOptions(
  currentValue: number | null,
): { value: number; label: string }[] {
  const options: { value: number; label: string }[] = [];
  for (
    let minutes = SEND_WINDOW_EARLIEST_MINUTES;
    minutes <= SEND_WINDOW_LATEST_MINUTES;
    minutes += SEND_WINDOW_STEP_MINUTES
  ) {
    options.push({ value: minutes, label: formatSendWindowTime(minutes) });
  }
  if (
    isValidSendWindowMinute(currentValue) &&
    (currentValue % SEND_WINDOW_STEP_MINUTES !== 0 ||
      currentValue < SEND_WINDOW_EARLIEST_MINUTES ||
      currentValue > SEND_WINDOW_LATEST_MINUTES)
  ) {
    options.push({
      value: currentValue,
      label: formatSendWindowTime(currentValue),
    });
    options.sort((left, right) => left.value - right.value);
  }
  return options;
}

export function isSendWindowMinuteInBounds(value: number): boolean {
  return (
    value >= SEND_WINDOW_EARLIEST_MINUTES && value <= SEND_WINDOW_LATEST_MINUTES
  );
}

export function sendWindowError(values: SendWindowFormValues): string | null {
  if (!isValidSendWindowMinute(values.start)) {
    return "Choose a start time.";
  }
  if (!isValidSendWindowMinute(values.end)) {
    return "Choose an end time.";
  }
  if (
    !isSendWindowMinuteInBounds(values.start) ||
    !isSendWindowMinuteInBounds(values.end)
  ) {
    return `Send window times must be between ${formatSendWindowTime(SEND_WINDOW_EARLIEST_MINUTES)} and ${formatSendWindowTime(SEND_WINDOW_LATEST_MINUTES)}.`;
  }
  if (values.start >= values.end) {
    return "Start time must be before the end time.";
  }
  return null;
}

export function buildSendWindowPatch(
  old: SendWindowFormValues,
  current: SendWindowFormValues,
): { site: SitePatch; updateMask: string[] } {
  const patch: SitePatch = {};
  const updateMask: string[] = [];

  if (sendWindowError(current) !== null) {
    return { site: patch, updateMask };
  }

  if (old.start !== current.start) {
    patch.sms_send_window_start = current.start;
    updateMask.push("sms_send_window_start");
  }
  if (old.end !== current.end) {
    patch.sms_send_window_end = current.end;
    updateMask.push("sms_send_window_end");
  }

  return { site: patch, updateMask };
}
